import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Loading, Text, Container } from "@nextui-org/react";
import { motion } from "framer-motion";
import moment from "moment";
import { database } from "../firebase";
import BanksLedgerHeader from "../components/banks-ledger/BanksLedgerHeader";
import BanksLedgerContent from "../components/banks-ledger/BanksLedgerContent";
import BanksLedgerFooter from "../components/banks-ledger/BanksLedgerFooter";
import "./BanksLedger.css";

const BanksLedger = () => {
  const [selectedMonth, setSelectedMonth] = useState(
    moment().format("YYYY-MM")
  );
  const [banks, setBanks] = useState([]);
  const [entries, setEntries] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);

      const [banksSnap, entriesSnap, paymentsSnap] = await Promise.all([
        database.collection("ledger_banks").get(),
        database
          .collection("ledger_entries")
          .where("month", "==", selectedMonth)
          .get(),
        database
          .collection("ledger_payments")
          .where("month", "==", selectedMonth)
          .get(),
      ]);

      // Filter active non-deleted banks
      const bankDocs = [];
      banksSnap.forEach((d) => {
        const data = d.data();
        if (!data.deleted) {
          bankDocs.push(data);
        }
      });
      bankDocs.sort((a, b) => (b.created || "").localeCompare(a.created || ""));

      // Entries list
      const entryDocs = entriesSnap.docs.map((d) => d.data());
      entryDocs.sort((a, b) => (a.created || "").localeCompare(b.created || ""));

      // Payments list
      const paymentDocs = paymentsSnap.docs.map((d) => d.data());
      paymentDocs.sort((a, b) => (b.created || "").localeCompare(a.created || ""));

      setBanks(bankDocs);
      setEntries(entryDocs);
      setPayments(paymentDocs);
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Group entries by bankId
  const entriesMap = useMemo(() => {
    const map = {};
    entries.forEach((e) => {
      if (!map[e.bankId]) {
        map[e.bankId] = [];
      }
      map[e.bankId].push(e);
    });
    return map;
  }, [entries]);

  // Grand Total of all entries across all banks for selected month
  const grandTotal = useMemo(() => {
    return entries.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  }, [entries]);

  // Total amount paid so far
  const totalPaid = useMemo(() => {
    return payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  }, [payments]);

  // Remaining balance to be paid
  const finalBalance = useMemo(() => {
    return Math.max(0, grandTotal - totalPaid);
  }, [grandTotal, totalPaid]);

  // Calculate status: "paid" | "partial" | "pending"
  const status = useMemo(() => {
    if (payments.length === 0) {
      return "pending";
    }

    const latestPaymentCreated = payments[0]?.created || "";
    const hasEntriesAfterPayment = entries.some(
      (e) => (e.created || "") > latestPaymentCreated
    );

    if (hasEntriesAfterPayment) {
      return "partial";
    }

    return "paid";
  }, [payments, entries]);

  // Handle Mark as Paid
  const handleMarkAsPaid = async () => {
    if (finalBalance <= 0) return;

    const newPaymentRef = database.collection("ledger_payments").doc();
    await newPaymentRef.set({
      _id: newPaymentRef.id,
      month: selectedMonth,
      amount: finalBalance,
      created: moment().toISOString(),
    });

    await fetchData();
  };

  return (
    <motion.div>
      <BanksLedgerHeader
        status={status}
        selectedMonth={selectedMonth}
        onMonthChange={setSelectedMonth}
      />

      <div className="banksLedgerDiv">
        <div style={{ width: "100%", paddingBottom: "30px" }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", paddingTop: "40px" }}>
              <Loading type="spinner" />
            </div>
          ) : error ? (
            <Text color="error" css={{ textAlign: "center", py: "20px" }}>
              An error occurred while loading ledger data.
            </Text>
          ) : (
            <>
              <BanksLedgerContent
                banks={banks}
                entriesMap={entriesMap}
                payments={payments}
                grandTotal={grandTotal}
                totalPaid={totalPaid}
                finalBalance={finalBalance}
              />
              <BanksLedgerFooter
                finalBalance={finalBalance}
                onMarkAsPaid={handleMarkAsPaid}
              />
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default BanksLedger;
